// Centralized constants for equipment, memory card, and asset registration
// Used by:
// - views/feature/adminTools/registerEquipments.jsx → equipment (types, models, manufacturers, serialNumberRequiredTypes, descriptions)
// - views/feature/adminTools/registerMemoryCard.jsx → memoryCard (types, manufacturers, sizes)
// - views/feature/adminTools/registerAssets.jsx     → asset (storageSizes, manufacturers, types)
// - views/feature/MemoryCard/index.jsx              → memoryCard (typeFilters, sizeFilters, statusFilters, subStatusFilters)

const equipmentConstants = {
	// ============================================================
	// MEMORY CARD CONSTANTS
	// ============================================================
	memoryCard: {
		types: [
			{ label: 'SD', value: 'SD' },
			{ label: 'SSD', value: 'SSD' },
			{ label: 'SSDmini', value: 'SSDmini' },
			{ label: 'SDHC', value: 'SDHC' },
			{ label: 'XQD', value: 'XQD' },
			{ label: 'MicroSD', value: 'MicroSD' },
			{ label: 'ToughSD', value: 'ToughSD' },
		],
		manufacturers: [
			{ label: 'Sandisk', value: 'Sandisk' },
			{ label: 'AtomX', value: 'AtomX' },
			{ label: 'Nextorage', value: 'Nextorage' },
			{ label: 'ATOMOS', value: 'ATOMOS' },
			{ label: 'Sony', value: 'Sony' },
			{ label: 'Lexar', value: 'Lexar' },
			{ label: 'Acer', value: 'Acer' },
			{ label: 'Nikon', value: 'Nikon' },
		],
		sizes: [
			{ label: '8GB', value: '8GB' },
			{ label: '16GB', value: '16GB' },
			{ label: '32GB', value: '32GB' },
			{ label: '64GB', value: '64GB' },
			{ label: '120GB', value: '120GB' },
			{ label: '128GB', value: '128GB' },
			{ label: '240GB', value: '240GB' },
			{ label: '500GB', value: '500GB' },
			{ label: '512GB', value: '512GB' },
			{ label: '1024GB', value: '1024GB' },
		],
		// Filter variants used on MemoryCard list page (key/name format)
		typeFilters: [
			{ key: 'SD', name: 'SD' },
			{ key: 'SSD', name: 'SSD' },
			{ key: 'SSDmini', name: 'SSD Mini' },
			{ key: 'SDDmini', name: 'SDD Mini' },
			{ key: 'SDHC', name: 'SDHC' },
			{ key: 'XQD', name: 'XQD' },
			{ key: 'MicroSD', name: 'Micro SD' },
			{ key: 'ToughSD', name: 'Tough SD' },
		],
		sizeFilters: [
			{ key: '8GB', name: '8GB' },
			{ key: '16GB', name: '16GB' },
			{ key: '32GB', name: '32GB' },
			{ key: '64GB', name: '64GB' },
			{ key: '120GB', name: '120GB' },
			{ key: '128GB', name: '128GB' },
			{ key: '240GB', name: '240GB' },
			{ key: '500GB', name: '500GB' },
			{ key: '512GB', name: '512GB' },
			{ key: '1024GB', name: '1024GB' },
		],
		statusFilters: [
			{ key: 'ready', name: 'Ready' },
			{ key: 'busy', name: 'Busy' },
		],
		subStatusFilters: [
			{ key: 'ready', name: 'Ready' },
			{ key: 'inShoot', name: 'In Shoot' },
			{ key: 'copyPending', name: 'Copy Pending' },
			{ key: 'verificationPending', name: 'Verification Pending' },
			{ key: 'releasePending', name: 'Release Pending' },
		],
	},

	// ============================================================
	// ASSET (HARD DRIVE / OFFICE) CONSTANTS
	// ============================================================
	asset: {
		storageSizes: [
			{ value: 4, label: '4 TB' },
			{ value: 1, label: '1 TB' },
			{ value: 2, label: '2 TB' },
			{ value: 3, label: '3 TB' },
			{ value: 5, label: '5 TB' },
			{ value: 6, label: '6 TB' },
			{ value: 8, label: '8 TB' },
			{ value: 10, label: '10 TB' },
			{ value: 12, label: '12 TB' },
			{ value: 16, label: '16 TB' },
		],
		manufacturers: [
			{ value: 'seagate', label: 'Seagate' },
			{ value: 'transcend', label: 'Transcend' },
			{ value: 'wd', label: 'WD' },
			{ value: 'gDrive', label: 'gDrive' },
			{ value: 'hp', label: 'HP' },
		],
		types: [
			{ label: 'Keyboard', value: 'keyboard' },
			{ label: 'Mouse', value: 'mouse' },
			{ label: 'Memory Card Reader', value: 'memoryCardReader' },
			{ label: 'Hub', value: 'hub' },
			{ label: 'iMac', value: 'iMac' },
			{ label: 'MacBook', value: 'macbook' },
			{ label: 'Mobile Phone', value: 'mobilePhone' },
			{ label: 'Sim', value: 'sim' },
			{ label: 'Monitor', value: 'monitor' },
			{ label: 'CPU', value: 'CPU' },
			{ label: 'Headphone', value: 'headphone' },
			{ label: 'Adapter', value: 'adapter' },
			{ label: 'Chair', value: 'chair' },
			{ label: 'Laptop', value: 'laptop' },
			{ label: 'Hard Drive', value: 'harddrive' },
			{ label: 'iPad', value: 'iPad' },
			{ label: 'Tablet', value: 'tablet' },
			{ label: 'Barcode scanner', value: 'barcodeScanner' },
			{ label: 'Printer', value: 'printer' },
			{ label: 'Mac Studio', value: 'macStudio' },
			{ label: 'Drawing Tablet', value: 'drawingTable' },
			{ label: 'Email', value: 'email' },
			{ label: 'Subscription', value: 'subscription' },
		],
	},

	// ============================================================
	// EQUIPMENT CONSTANTS
	// ============================================================
	equipment: {
		types: [
			{ label: 'Atomos', value: 'atomos' },
			{ label: 'Gimble Battery', value: 'gimbleBattery' },
			{ label: 'Lens Mount', value: 'lensMount' },
			{ label: 'Camera Mic Bag', value: 'cameraMicBag' },
			{ label: 'Led Light', value: 'ledLight' },
			{ label: 'Mic', value: 'mic' },
			{ label: 'Speed Light', value: 'speedLight' },
			{ label: 'Mic Bag', value: 'micBag' },
			{ label: 'Gimble Tripod', value: 'gimbleTripod' },
			{ label: 'Magmod', value: 'magmod' },
			{ label: 'Battery Adapter', value: 'batteryAdapter' },
			{ label: 'Extension Box', value: 'extensionBox' },
			{ label: 'Flash', value: 'flash' },
			{ label: 'Drone Charger', value: 'droneCharger' },
			{ label: 'ND Filter', value: 'ndFilter' },
			{ label: 'Led Light Stick', value: 'ledLightStick' },
			{ label: 'Flash Battery', value: 'flashBattery' },
			{ label: 'Camera Mic', value: 'cameraMic' },
			{ label: 'Lens', value: 'lens' },
			{ label: 'Flash Umbrella Clamp', value: 'flashUmbrellaClamp' },
			{ label: 'Drone', value: 'drone' },
			{ label: 'Charger Cable', value: 'chargerCable' },
			{ label: 'Bag', value: 'bag' },
			{ label: 'Gimble Case', value: 'gimbleCase' },
			{ label: 'Sungun', value: 'sungun' },
			{ label: 'Drone Car Charger', value: 'droneCarCharger' },
			{ label: 'Gimble', value: 'gimble' },
			{ label: 'Soft Box', value: 'softBox' },
			{ label: 'Adapter', value: 'adapter' },
			{ label: 'Charger', value: 'charger' },
			{ label: 'Battery', value: 'battery' },
			{ label: 'Battery Charger', value: 'batteryCharger' },
			{ label: 'Trigger', value: 'trigger' },
			{ label: 'Camera', value: 'camera' },
			{ label: 'Tripod', value: 'tripod' },
			{ label: 'Bowens Mount', value: 'bowensMount' },
			{ label: 'Light Stand', value: 'lightStand' },
			{ label: 'NANLITE', value: 'nanLite' },
			{ label: 'Double Strap', value: 'doubleStrap' },
			{ label: 'Reflector', value: 'reflector' },
			{ label: 'Reflector', value: 'reflector' },
			{ label: 'Monopod', value: 'monopod' },
			{ label: 'Spot Light', value: 'spotLight' },
			{ label: 'Double Strap', value: 'doubleStrap' },
			{ label: 'Snapgram Photo Booth', value: 'snapgramPhotoBooth' },
			{ label: 'Wooden Booth', value: 'woodenBooth' },
			{ label: 'Printer', value: 'printer' },
		],
		models: [
			{ type: 'woodenBooth', label: 'DSLR', value: 'DSLR' },
			{ type: 'printer', label: '100397', value: '100397' },
			{ type: 'printer', label: '100540', value: '100540' },
			{ type: 'snapgramPhotoBooth' },
			{ type: 'camera', label: 'Z6', value: 'Z6' },
			{ type: 'camera', label: 'Z6 II', value: 'Z6 II' },
			{ type: 'camera', label: 'Z7', value: 'Z7' },
			{ type: 'camera', label: 'ZR', value: 'ZR' },
			{ type: 'drone', label: 'Mavic 2 Pro', value: 'Mavic 2 Pro' },
			{ type: 'lens', label: '50/1.8G', value: '50/1.8G' },
			{ type: 'lens', label: '85/1.8G', value: '85/1.8G' },
			{ type: 'lens', label: '70-200/2.8G', value: '70-200/2.8G' },
			{ type: 'lens', label: '14-24/2.8G', value: '14-24/2.8G' },
			{ type: 'lens', label: '35/1.8G', value: '35/1.8G' },
			{ type: 'lens', label: '24-70/2.8G', value: '24-70/2.8G' },
			{ type: 'lens', label: '50/1.4G', value: '50/1.4G' },
			{ type: 'lens', label: '85/1.4G', value: '85/1.4G' },
			{ type: 'lens', label: '105/2.8G', value: '105/2.8G' },
			{ type: 'lens', label: '135/2D', value: '135/2D' },
			{ type: 'lens', label: '70-180/2.8S', value: '70-180/2.8S' },
			{ type: 'lens', label: '24-70/2.8S', value: '24-70/2.8S' },
			{ type: 'lens', label: '50/1.8S', value: '50/1.8S' },
			{ type: 'lens', label: '105/2.8S', value: '105/2.8S' },
			{ type: 'lens', label: '85/1.8S', value: '85/1.8S' },
			{ type: 'lens', label: '35/1.8S', value: '35/1.8S' },
			{ type: 'lens', label: '70-200/2.8S', value: '70-200/2.8S' },
			{ type: 'lens', label: '24/1.8S', value: '24/1.8S' },
			{ type: 'lens', label: '50/1.2S', value: '50/1.2S' },
			{ type: 'lens', label: '28-75/2.8S', value: '28-75/2.8S' },
			{ type: 'lens', label: '17-28/2.8S', value: '17-28/2.8S' },
			{ type: 'lens', label: '24-70/4S', value: '24-70/4S' },
			{ type: 'lens', label: '24-120/4S', value: '24-120/4S' },
			{ type: 'lens', label: '50/1.8S', value: '50/1.8S' },
			{ type: 'lens', label: '105mm/2.8S', value: '105mm/2.8S' },
			{ type: 'lens', label: '28-75mm/2.8S', value: '28-75mm/2.8S' },
			{ type: 'lens', label: '14-24mm', value: '14-24mm' },
			{ type: 'lens', label: '50/1.2S', value: '50/1.2S' },
			{ type: 'lens', label: '40/2S', value: '40/2S' },
			{ type: 'tripod', label: 'AT-7402A', value: 'AT-7402A' },
			{ type: 'tripod', label: 'MVGBF-CF', value: 'MVGBF-CF' },
			{ type: 'battery', label: 'EN-EL 15', value: 'EN-EL 15' },
			{ type: 'battery', label: 'EN-EL 15B', value: 'EN-EL 15B' },
			{ type: 'batteryCharger', label: 'MH-25a', value: 'MH-25a' },
			{ type: 'batteryCharger', label: 'DUC-006', value: 'DUC-006' },
			{ type: 'gimble', label: 'DJI RSC 2', value: 'DJI RSC 2' },
			{ type: 'gimble', label: 'Ronin SC', value: 'Ronin SC' },
			{ type: 'flash', label: 'V860-2C', value: 'V860-2C' },
			{ type: 'flash', label: 'AD200', value: 'AD200' },
			{ type: 'flash', label: 'AD600 Pro', value: 'AD600 Pro' },
			{ type: 'trigger', label: 'X2TN', value: 'X2TN' },
			{ type: 'trigger', label: 'X Pro N', value: 'X Pro N' },
			{ type: 'charger', label: 'B10 charger', value: 'B10 charger' },
			{ type: 'adapter', label: 'C400P', value: 'C400P' },
			{ type: 'softBox', label: 'AD-S60S', value: 'AD-S60S' },
			{ type: 'lightStand', label: 'Simpex', value: 'Simpex' },
			{ type: 'lightStand', label: 'Sonia', value: 'Sonia' },
			{ type: 'ledLightStick', label: 'LC500', value: 'LC500' },
			{ type: 'battery' },
			{ type: 'cameraMicBag' },
			{ type: 'gimbleTripod' },
			{ type: 'batteryCharger', label: 'VC26', value: 'VC26' },
			{ type: 'camera', label: 'Z7 II', value: 'Z7 II' },
			{ type: 'lens', label: '70-180mm/2.8S', value: '70-180mm/2.8S' },
			{ type: 'lens', label: '24-70/2.8S', value: '24-70/2.8S' },
			{ type: 'battery', label: 'VB18', value: 'VB18' },
			{ type: 'batteryCharger', label: 'DUC-008', value: 'DUC-008' },
			{ type: 'battery', label: 'EN-EL 15B', value: 'EN-EL 15B' },
			{ type: 'ledLight' },
			{ type: 'flash', label: 'Mitros+', value: 'Mitros+' },
			{ type: 'tripod', label: 'EH730', value: 'EH730' },
			{ type: 'bag', label: 'BP-350 AW II', value: 'BP-350 AW II' },
			{ type: 'battery', label: 'NP-FZ100', value: 'NP-FZ100' },
			{ type: 'softBox', label: 'AD-S60S', value: 'AD-S60S' },
			{ type: 'chargerCable', label: 'P2012-L4', value: 'P2012-L4' },
			{ type: 'gimbleTripod', label: 'MVMXPROA4', value: 'MVMXPROA4' },
			{ type: 'speedLight', label: 'AD400 Pro', value: 'AD400 Pro' },
			{ type: 'flash', label: 'B1', value: 'B1' },
			{ type: 'bag' },
			{ type: 'batteryCharger', label: 'C29', value: 'C29' },
			{ type: 'lens', label: '135mm TS-E', value: '135mm TS-E' },
			{ type: 'battery', label: 'F980', value: 'F980' },
			{ type: 'lens', label: '28mm/2.8S', value: '28mm/2.8S' },
			{ type: 'ndFilter', label: '49mm', value: '49mm' },
			{ type: 'chargerCable', label: '2*0.75sqmm', value: '2*0.75sqmm' },
		],
		manufacturers: [
			{ label: 'DNP', value: 'DNP' },
			{ label: 'EveryBooth', value: 'everyBooth' },
			{ label: 'SalsaBooth', value: 'SalsaBooth' },
			{ label: 'Nikkor', value: 'Nikkor' },
			{ label: 'NanLite', value: 'NanLite' },
			{ label: 'Mandee', value: 'Mandee' },
			{ label: 'GoldMedal', value: 'GoldMedal' },
			{ label: 'Welborn', value: 'Welborn' },
			{ label: 'TyFy', value: 'TyFy' },
			{ label: 'Moza', value: 'Moza' },
			{ label: 'LineTek', value: 'LineTek' },
			{ label: 'Li-ion', value: 'Li-ion' },
			{ label: 'Think Tank', value: 'Think Tank' },
			{ label: 'DJI Ronin', value: 'DJI Ronin' },
			{ label: 'Manfrotto', value: 'Manfrotto' },
			{ label: 'Godox', value: 'Godox' },
			{ label: 'E-Image', value: 'E-Image' },
			{ label: 'Ninja V', value: 'Ninja V' },
			{ label: 'Ronin', value: 'Ronin' },
			{ label: 'DJI PRO', value: 'DJI PRO' },
			{ label: 'YH', value: 'YH' },
			{ label: 'DJI Mavic 2 Pro', value: 'DJI Mavic 2 Pro' },
			{ label: 'GoMe', value: 'GoMe' },
			{ label: 'Yongnu', value: 'Yongnu' },
			{ label: 'Sony', value: 'Sony' },
			{ label: 'K & F Concept', value: 'K & F Concept' },
			{ label: 'DigiTek', value: 'DigiTek' },
			{ label: 'Mobius', value: 'Mobius' },
			{ label: 'Envie', value: 'Envie' },
			{ label: 'Local', value: 'Local' },
			{ label: 'Profoto', value: 'Profoto' },
			{ label: 'Canon', value: 'Canon' },
			{ label: 'Longwell - P', value: 'Longwell - P' },
			{ label: 'Osaka', value: 'Osaka' },
			{ label: 'Nikon', value: 'Nikon' },
			{ label: 'Rode', value: 'Rode' },
			{ label: 'Simpex', value: 'Simpex' },
			{ label: 'Kewals', value: 'Kewals' },
			{ label: 'Lowepro', value: 'Lowepro' },
			{ label: 'Hifffin', value: 'Hifffin' },
			{ label: 'DJI Mavic Pro Platinum', value: 'DJI Mavic Pro Platinum' },
			{ label: 'Pinball', value: 'Pinball' },
			{ label: 'Vanguard', value: 'Vanguard' },
			{ label: 'Photopro', value: 'Photopro' },
			{ label: 'Fitech', value: 'Fitech' },
			{ label: 'DJI', value: 'DJI' },
			{ label: 'Phottix', value: 'Phottix' },
			{ label: 'Manfortto', value: 'Manfortto' },
			{ label: 'Ninja V +', value: 'Ninja V +' },
			{ label: 'Hoeton', value: 'Hoeton' },
			{ label: 'Black Rapid', value: 'blackRapid' },
			{ label: 'Sennhesiser', value: 'sennhesiser' },
			{ label: 'Nisi', value: 'Nisi' },
		],
		serialNumberRequiredTypes: [
			'atomos',
			'batteryCharger',
			'battery',
			'camera',
			'gimble',
			'lens',
		],
		descriptions: [
			{ type: 'printer', label: 'Snapgram Printer', value: 'Snapgram Printer' },
			{ type: 'woodenBooth', label: 'Snapgram Wooden Booth', value: 'Snapgram Wooden Booth' },
			{ type: 'snapgramPhotoBooth', label: 'Snapgram Salsa Photo Booth', value: 'Snapgram Salsa Photo Booth' },
			{ type: 'ledLight', label: 'Digitek LED Light', value: 'Digitek LED Light' },
			{ type: 'lightStand', label: 'Simpex Light Stand', value: 'Simpex Light Stand' },
			{ type: 'lightStand', label: 'Sonia Light Stand', value: 'Sonia Light Stand' },
			{ type: 'doubleStrap', label: 'Mandee Leather Strap Double Strap', value: 'Mandee Leather Strap Double Strap' },
			{ type: 'flash', label: 'Godox V860-2C Flash', value: 'Godox V860-2C Flash' },
			{ type: 'spotLight', label: 'Godox ML60 Spot Light', value: 'Godox ML60 Spot Light' },
			{ type: 'monopod', label: 'Monopod Osaka VH25', value: 'Monopod Osaka VH25' },
			{ type: 'micBag', label: 'Rode Mic Bag(Includes 3 mics)', value: 'Rode Mic Bag(Includes 3 mics)' },
			{ type: 'nanLite', label: 'NANLite PavoTube 15c', value: 'NANLite PavoTube 15c' },
			{ type: 'nanLite', label: 'NANLite PavoTube 30c', value: 'NANLite PavoTube 30c' },
			{ type: 'nanLite', label: 'NANLite PavoTube II 30x', value: 'NANLite PavoTube II 30x' },
			{ type: 'flash', label: 'Profoto A1 flash', value: 'Profoto A1 flash' },
			{ type: 'flash', label: 'Godox AD600 Pro', value: 'Godox AD600 Pro' },
			{ type: 'reflector', label: 'Hiffin 32.5inch SPL Reflector', value: 'Hiffin 32.5inch SPL Reflector' },
			{ type: 'battery', label: 'Godox WB400P battery', value: 'Godox WB400P battery' },
			{ type: 'tripod', label: 'E-Image AT-7402A', value: 'E-Image AT-7402A' },
			{ type: 'lens', label: 'Nikon 45mm/2.8D', value: 'Nikon 45mm/2.8D' },
			{ type: 'lens', label: 'Nikkor Z 17-28/2.8S', value: 'Nikkor Z 17-28/2.8S' },
			{ type: 'tripod', label: 'Manfrotto MVGBF-CF Tripod', value: 'Manfrotto MVGBF-CF Tripod' },
			{ type: 'lens', label: 'Nikkor Z 28mm/2.8S', value: 'Nikkor Z 28mm/2.8S' },
			{ type: 'charger', label: 'Profoto B10 Charger', value: 'Profoto B10 Charger' },
			{ type: 'flash', label: 'Godox V1N Flash', value: 'Godox V1N Flash' },
			{ type: 'bag', label: 'Mobius Shoulder bag', value: 'Mobius Shoulder bag' },
			{ type: 'gimbleBattery', label: 'DJI Ronin RSCG18 Gimble Battery', value: 'DJI Ronin RSCG18 Gimble Battery' },
			{ type: 'trigger', label: 'Godox X2TN Trigger', value: 'Godox X2TN Trigger' },
			{ type: 'lens', label: 'Nikkor Z 50mm/1.2S', value: 'Nikkor Z 50mm/1.2S' },
			{ type: 'bag', label: 'Godox AD400 Pro Bag', value: 'Godox AD400 Pro Bag' },
			{ type: 'battery', label: 'Godox WB400P Battery', value: 'Godox WB400P Battery' },
			{ type: 'battery', label: 'Godox WB29 Battery', value: 'Godox WB29 Battery' },
			{ type: 'lightStand', label: 'Photopro Light Stand', value: 'Photopro Light Stand' },
			{ type: 'battery', label: 'Osaka Small Battery', value: 'Osaka Small Battery' },
			{ type: 'bag', label: 'Lowepro Bag', value: 'Lowepro Bag' },
			{ type: 'drone', label: 'DJI Mavic Pro Platinum M1X Drone', value: 'DJI Mavic Pro Platinum M1X Drone' },
			{ type: 'lens', label: 'Nikon 14-24mm/2.8G', value: 'Nikon 14-24mm/2.8G' },
			{ type: 'bag', label: 'Pinball Lights Bag', value: 'Pinball Lights Bag' },
			{ type: 'bag', label: 'Godx AD400 speedLight Bag', value: 'Godx AD400 speedLight Bag' },
			{ type: 'flashUmbrellaClamp', label: 'Photopro Flash Umbrella Clamp', value: 'Photopro Flash Umbrella Clamp' },
			{ type: 'lensMount', label: 'Nikkor FTZ', value: 'Nikkor FTZ' },
			{ type: 'lensMount', label: 'Nikon FTZ II', value: 'Nikon FTZ II' },
			{ type: 'chargerCable', label: 'Longwell - P 2*0.75sqmm Charger Cable', value: 'Longwell - P 2*0.75sqmm Charger Cable' },
			{ type: 'flash', label: 'Profoto B10 Flash', value: 'Profoto B10 Flash' },
			{ type: 'ndFilter', label: 'YH 49mm ND Filter', value: 'YH 49mm ND Filter' },
			{ type: 'chargerCable', label: 'Godox P2012-L4 Charger cable', value: 'Godox P2012-L4 Charger cable' },
			{ type: 'gimbleBattery', label: 'Ronin SC Gimble Battery', value: 'Ronin SC Gimble Battery' },
			{ type: 'gimble', label: 'DJI PRO RSC 2 Gimble', value: 'DJI PRO RSC 2 Gimble' },
			{ type: 'bag', label: 'Fitech Gimble Bag', value: 'Fitech Gimble Bag' },
			{ type: 'lens', label: 'Canon 135mm TS-E', value: 'Canon 135mm TS-E' },
			{ type: 'lens', label: 'Canon 135mm/1.8s', value: 'Canon 135mm/1.8s' },
			{ type: 'batteryCharger', label: 'Sony FS5 Battery Charger', value: 'Sony FS5 Battery Charger' },
			{ type: 'droneCarCharger', label: 'DJI C3S80 Mavic Car Drone Charger', value: 'DJI C3S80 Mavic Car Drone Charger' },
			{ type: 'trigger', label: 'Profoto TTL-N Trigger', value: 'Profoto TTL-N Trigger' },
			{ type: 'batteryCharger', label: 'Godox Flash Charger', value: 'Godox Flash Charger' },
			{ type: 'tripod', label: 'Manfortto MVMXPROA4 Tripod', value: 'Manfortto MVMXPROA4 Tripod' },
			{ type: 'gimble', label: 'Ronin DJI RSC 2 Gimble', value: 'Ronin DJI RSC 2 Gimble' },
			{ type: 'cameraMic', label: 'Rode Camera Mic', value: 'Rode Camera Mic' },
			{ type: 'chargerCable', label: 'Local 2*0.75sqmm Charger Cable', value: 'Local 2*0.75sqmm Charger Cable' },
			{ type: 'battery', label: 'Godox VB18 Battery', value: 'Godox VB18 Battery' },
			{ type: 'battery', label: 'Godox Battery', value: 'Godox Battery' },
			{ type: 'lens', label: 'Nikon 17-28mm/2.8S', value: 'Nikon 17-28mm/2.8S' },
			{ type: 'batteryCharger', label: 'Simpex Dual Digital Battery Charger', value: 'Simpex Dual Digital Battery Charger' },
			{ type: 'gimble', label: 'DJI Ronin RSC Gimble', value: 'DJI Ronin RSC Gimble' },
			{ type: 'bag', label: 'Godox LC500R LED Light Stick Bag', value: 'Godox LC500R LED Light Stick Bag' },
			{ type: 'trigger', label: 'Profoto TTL-N B10 Trigger', value: 'Profoto TTL-N B10 Trigger' },
			{ type: 'batteryCharger', label: 'TyFy Battery Charger', value: 'TyFy Battery Charger' },
			{ type: 'batteryCharger', label: 'Profoto Battery Charger', value: 'Profoto Battery Charger' },
			{ type: 'speedLight', label: 'Godox AD400 Pro Speed Light', value: 'Godox AD400 Pro Speed Light' },
			{ type: 'battery', label: 'Osaka NP-F750 Small Battery', value: 'Osaka NP-F750 Small Battery' },
			{ type: 'atomos', label: 'Ninja V Atomos', value: 'Ninja V Atomos' },
			{ type: 'lens', label: 'Nikkor Z 24-70mm/2.8S', value: 'Nikkor Z 24-70mm/2.8S' },
			{ type: 'lens', label: 'Nikkor Z 70-180mm/2.8S', value: 'Nikkor Z 70-180mm/2.8S' },
			{ type: 'battery', label: 'Profoto100440 Battery', value: 'Profoto100440 Battery' },
			{ type: 'battery', label: 'Simpex NP-F770 Battery', value: 'Simpex NP-F770 Battery' },
			{ type: 'batteryCharger', label: 'Nikon Battery Charger MH-25a', value: 'Nikon Battery Charger MH-25a' },
			{ type: 'droneCarCharger', label: 'DJI C4S80 Mavic 2 Car Drone Charger', value: 'DJI C4S80 Mavic 2 Car Drone Charger' },
			{ type: 'batteryCharger', label: 'Godox C400P B1 Flash Battery Charger', value: 'Godox C400P B1 Flash Battery Charger' },
			{ type: 'batteryCharger', label: 'TyFy Dual Digital Battery Charger', value: 'TyFy Dual Digital Battery Charger' },
			{ type: 'cameraMicBag', label: 'Moza Camera Mic Bag', value: 'Moza Camera Mic Bag' },
			{ type: 'extensionBox', label: 'Kewals Estension Box', value: 'Kewals Estension Box' },
			{ type: 'extensionBox', label: 'Kewals Extension Box(6 Port)', value: 'Kewals Extension Box(6 Port)' },
			{ type: 'ledLightStick', label: 'Godox LC500R LED Light Stick', value: 'Godox LC500R LED Light Stick' },
			{ type: 'ledLightStick', label: 'Yongnu YN360 III LED Light Stick', value: 'Yongnu YN360 III LED Light Stick' },
			{ type: 'lens', label: 'Nikkor Z 40mm/2S', value: 'Nikkor Z 40mm/2S' },
			{ type: 'lens', label: 'Nikkor Z 28-75mm/2.8S', value: 'Nikkor Z 28-75mm/2.8S' },
			{ type: 'extensionBox', label: 'Hoeton Extension Box(10 ports)', value: 'Hoeton Extension Box(10 ports)' },
			{ type: 'magmod', label: 'Godox AD400 magmod', value: 'Godox AD400 magmod' },
			{ type: 'bag', label: 'GoMe Lights Bag', value: 'GoMe Lights Bag' },
			{ type: 'camera', label: 'Nikon Z7 Camera', value: 'Nikon Z7 Camera' },
			{ type: 'battery', label: 'Godox AD300 Pro Battery', value: 'Godox AD300 Pro Battery' },
			{ type: 'bag', label: 'Fitech Gimble bag', value: 'Fitech Gimble bag' },
			{ type: 'lens', label: 'Nikkor Z 24-70mm/4S', value: 'Nikkor Z 24-70mm/4S' },
			{ type: 'gimble', label: 'Manfrotto MVG 220 Gimble', value: 'Manfrotto MVG 220 Gimble' },
			{ type: 'gimble', label: 'DJI RSC 3 Gimble', value: 'DJI RSC 3 Gimble' },
			{ type: 'gimble', label: 'DJI RSC 3Pro Gimble', value: 'DJI RSC 3Pro Gimble' },
			{ type: 'battery', label: 'Profoto100397 Battery', value: 'Profoto100397 Battery' },
			{ type: 'lens', label: 'Nikkor Z 70-200mm/2.8S', value: 'Nikkor Z 70-200mm/2.8S' },
			{ type: 'lens', label: 'Nikkor Z 17-28mm/2.8S', value: 'Nikkor Z 17-28mm/2.8S' },
			{ type: 'battery', label: 'Simpex Small Battery', value: 'Simpex Small Battery' },
			{ type: 'battery', label: 'Osaka Big Battery', value: 'Osaka Big Battery' },
			{ type: 'lens', label: 'Nikkor Z 105mm/2.8S', value: 'Nikkor Z 105mm/2.8S' },
			{ type: 'lens', label: 'Nikkor Z 50/1.2S', value: 'Nikkor Z 50/1.2S' },
			{ type: 'batteryCharger', label: 'Sony Battery Charger', value: 'Sony Battery Charger' },
			{ type: 'trigger', label: 'Godox X Pro N Trigger', value: 'Godox X Pro N Trigger' },
			{ type: 'lens', label: 'Nikkor Z 50mm/2.8S', value: 'Nikkor Z 50mm/2.8S' },
			{ type: 'flash', label: 'Godox AD300Pro Flash', value: 'Godox AD300Pro Flash' },
			{ type: 'chargerCable', label: 'LineTek 2*0.75sqmm Charger Cable', value: 'LineTek 2*0.75sqmm Charger Cable' },
			{ type: 'charger', label: 'Godox LC500 LED Light Stick Charger', value: 'Godox LC500 LED Light Stick Charger' },
			{ type: 'camera', label: 'Nikon Z7 II Camera', value: 'Nikon Z7 II Camera' },
			{ type: 'flash', label: 'Yongnu YN685 Flash', value: 'Yongnu YN685 Flash' },
			{ type: 'bag', label: 'Lowepro BP 450 AW II Pro Treker Bag', value: 'Lowepro BP 450 AW II Pro Treker Bag' },
			{ type: 'bag', label: 'Vanguard Trolley Bag(wheels)', value: 'Vanguard Trolley Bag(wheels)' },
			{ type: 'batteryCharger', label: 'Welborn Dual Digital Battery Charger', value: 'Welborn Dual Digital Battery Charger' },
			{ type: 'bowensMount', label: 'Godox AD400 Bowens Mount', value: 'Godox AD400 Bowens Mount' },
			{ type: 'batteryCharger', label: 'DigiTek Dual Digital Battery Charger', value: 'DigiTek Dual Digital Battery Charger' },
			{ type: 'lens', label: 'Nikkor Z 24/1.8S', value: 'Nikkor Z 24/1.8S' },
			{ type: 'lens', label: 'Nikkor F 14-24mm', value: 'Nikkor F 14-24mm' },
			{ type: 'droneCarCharger', label: 'DJI P1CH Mavic AIR Drone Charger', value: 'DJI P1CH Mavic AIR Drone Charger' },
			{ type: 'mic', label: 'DJI ASB01 Mic', value: 'DJI ASB01 Mic' },
			{ type: 'mic', label: 'Rode Wireless II Mic', value: 'Rode Wireless II Mic' },
			{ type: 'mic', label: 'ASBO1', value: 'ASBO1' },
			{ type: 'mic', label: 'DJI 2PC', value: 'DJI 2PC' },
			{ type: 'mic', label: 'DJI 3PC', value: 'DJI 3PC' },
			{ type: 'mic', label: 'Sennheiser MKE 600 Mic', value: 'Sennheiser MKE 600 Mic' },
			{ type: 'mic', label: 'Sennheiser MKE 400 Mic', value: 'Sennheiser MKE 400 Mic' },
			{ type: 'battery', label: 'Nikon Battery', value: 'Nikon Battery' },
			{ type: 'bag', label: 'Think Tank Bag(wheels)', value: 'Think Tank Bag(wheels)' },
			{ type: 'lens', label: 'Nikon 16-35mm/4G', value: 'Nikon 16-35mm/4G' },
			{ type: 'flash', label: 'Profoto A1 Flash', value: 'Profoto A1 Flash' },
			{ type: 'droneCharger', label: 'DJI F1C50 Drone Charger', value: 'DJI F1C50 Drone Charger' },
			{ type: 'droneCharger', label: 'DJI F2C60 Drone Charger', value: 'DJI F2C60 Drone Charger' },
			{ type: 'batteryCharger', label: 'Godox Battery Charger', value: 'Godox Battery Charger' },
			{ type: 'camera', label: 'Nikon Z6 Camera(supports Atomos)', value: 'Nikon Z6 Camera(supports Atomos)' },
			{ type: 'lens', label: 'Nikkor Z 50mm/1.8S', value: 'Nikkor Z 50mm/1.8S' },
			{ type: 'bag', label: 'Profoto Shoulder Bag', value: 'Profoto Shoulder Bag' },
			{ type: 'lens', label: 'Nikon 28-75mm/2.8S', value: 'Nikon 28-75mm/2.8S' },
			{ type: 'lens', label: 'Nikkor Z 85mm/1.8S', value: 'Nikkor Z 85mm/1.8S' },
			{ type: 'lens', label: 'Nikkor Z 85mm/1.2S', value: 'Nikkor Z 85mm/1.2S' },
			{ type: 'batteryCharger', label: 'Simpex Battery Charger', value: 'Simpex Battery Charger' },
			{ type: 'battery', label: 'Sony NP-FZ100 Battery', value: 'Sony NP-FZ100 Battery' },
			{ type: 'bag', label: 'Lowepro Shoulder Bag', value: 'Lowepro Shoulder Bag' },
			{ type: 'flash', label: 'Godox AD200 Flash', value: 'Godox AD200 Flash' },
			{ type: 'lens', label: 'Nikon 105mm/1.4E', value: 'Nikon 105mm/1.4E' },
			{ type: 'ndFilter', label: 'K & F Concept ND Filter', value: 'K & F Concept ND Filter' },
			{ type: 'gimbleCase', label: 'Ronin SC Gimble Case', value: 'Ronin SC Gimble Case' },
			{ type: 'lens', label: 'Nikkor 24mm/1.8S', value: 'Nikkor 24mm/1.8S' },
			{ type: 'lightStand', label: 'Manfrotto Light Stand', value: 'Manfrotto Light Stand' },
			{ type: 'extensionBox', label: 'GoldMedal Extension Box(1 Port)', value: 'GoldMedal Extension Box(1 Port)' },
			{ type: 'bag', label: 'Lowepro BP-350 AW II Pro Runner shoulder bag', value: 'Lowepro BP-350 AW II Pro Runner shoulder bag' },
			{ type: 'lens', label: 'Nikkor Z 105/2.8S', value: 'Nikkor Z 105/2.8S' },
			{ type: 'gimbleTripod', label: 'Manfortto MVMXPROA4 Tripod', value: 'Manfortto MVMXPROA4 Tripod' },
			{ type: 'lens', label: 'Nikkor Z 35mm/1.8S', value: 'Nikkor Z 35mm/1.8S' },
			{ type: 'lens', label: 'Nikkor Z 50/1.8S', value: 'Nikkor Z 50/1.8S' },
			{ type: 'flashBattery', label: 'Profoto B1 flash Battery', value: 'Profoto B1 flash Battery' },
			{ type: 'batteryCharger', label: 'Godox C29 AD300 Pro Battery Charger', value: 'Godox C29 AD300 Pro Battery Charger' },
			{ type: 'bag', label: 'Mobius Lights Bag', value: 'Mobius Lights Bag' },
			{ type: 'adapter', label: 'Godox C400P AC/DC Adapter', value: 'Godox C400P AC/DC Adapter' },
			{ type: 'ledLightStick', label: 'Godox LC500 LED Light Stick', value: 'Godox LC500 LED Light Stick' },
			{ type: 'droneCharger', label: 'DJI P1C50 Drone Charger', value: 'DJI P1C50 Drone Charger' },
			{ type: 'ndFilter', label: 'Hifffin ND Filter', value: 'Hifffin ND Filter' },
			{ type: 'lens', label: 'Nikkor Z 105/1.4E', value: 'Nikkor Z 105/1.4E' },
			{ type: 'lens', label: 'Nikkor Z MC 105/2.8S', value: 'Nikkor Z MC 105/2.8S' },
			{ type: 'softBox', label: 'Godox 80cm Softbox', value: 'Godox 80cm Softbox' },
			{ type: 'softBox', label: 'Profoto B10 Soft Box(honey comb, 1 diffusers)', value: 'Profoto B10 Soft Box(honey comb, 1 diffusers)' },
			{ type: 'softBox', label: 'Godox AD-S60S Soft Box(honey comb, 3 diffusers)', value: 'Godox AD-S60S Soft Box(honey comb, 3 diffusers)' },
			{ type: 'bag', label: 'Lowepro BP 450 AW II Pro Treker Bag(wheels)', value: 'Lowepro BP 450 AW II Pro Treker Bag(wheels)' },
			{ type: 'lens', label: 'Nikon 28mm/2.8S', value: 'Nikon 28mm/2.8S' },
			{ type: 'sungun', label: 'Photopro V-4 Sun Gun', value: 'Photopro V-4 Sun Gun' },
			{ type: 'lens', label: 'Nikkor Z 58/0.95S', value: 'Nikkor Z 58/0.95S' },
			{ type: 'flash', label: 'Profoto B1 flash', value: 'Profoto B1 flash' },
			{ type: 'batteryCharger', label: 'Li-ion Battery Charger', value: 'Li-ion Battery Charger' },
			{ type: 'camera', label: 'Nikon Z6 II Camera', value: 'Nikon Z6 II Camera' },
			{ type: 'camera', label: 'Nikon Z6 III Camera', value: 'Nikon Z6 III Camera' },
			{ type: 'camera', label: 'Nikon Zf Camera', value: 'Nikon Zf Camera' },
			{ type: 'camera', label: 'Nikon Z8 Camera', value: 'Nikon Z8 Camera' },
			{ type: 'bag', label: 'Think Tank Bag', value: 'Think Tank Bag' },
			{ type: 'bag', label: 'Pinball Shoulder bag', value: 'Pinball Shoulder bag' },
			{ type: 'camera', label: 'Nikon Z50 Camera', value: 'Nikon Z50 Camera' },
			{ type: 'camera', label: 'Nikon Z30 Camera', value: 'Nikon Z30 Camera' },
			{ type: 'charger', label: 'Godox LC500R LED Light Stick Charger', value: 'Godox LC500R LED Light Stick Charger' },
			{ type: 'batteryCharger', label: 'DigiTek Battery Charger', value: 'DigiTek Battery Charger' },
			{ type: 'lens', label: 'Nikkor 3.5-6.3mm', value: 'Nikkor 3.5-6.3mm' },
			{ type: 'gimble', label: 'DJI Ronin RSC 2 Gimble', value: 'DJI Ronin RSC 2 Gimble' },
			{ type: 'gimble', label: 'DJI Ronin RSC 1 Gimble', value: 'DJI Ronin RSC 1 Gimble' },
			{ type: 'battery', label: 'Profoto100440 B10 Battery', value: 'Profoto100440 B10 Battery' },
			{ type: 'lens', label: 'Nikon 14-30mm/4s', value: 'Nikon 14-30mm/4s' },
			{ type: 'lens', label: 'Nikkor Z 20mm/1.8S', value: 'Nikkor Z 20mm/1.8S' },
			{ type: 'lens', label: 'Nikkor Z 24-120mm/4S', value: 'Nikkor Z 24-120mm/4S' },
			{ type: 'lens', label: 'Nikkor Z 40/2S', value: 'Nikkor Z 40/2S' },
			{ type: 'camera', label: 'Nikon Z6 Camera', value: 'Nikon Z6 Camera' },
			{ type: 'softBox', label: 'Godox AD-S65S Soft Box(honey comb, 3 diffusers)', value: 'Godox AD-S65S Soft Box(honey comb, 3 diffusers)' },
			{ type: 'lens', label: 'Nikon 45mm/2.8ED', value: 'Nikon 45mm/2.8ED' },
			{ type: 'micBag', label: 'Rode Mic Bag(Includes 2 mics)', value: 'Rode Mic Bag(Includes 2 mics)' },
			{ type: 'batteryCharger', label: 'Envie Battery Charger', value: 'Envie Battery Charger' },
			{ type: 'ledLight', label: 'Simpex Flash Light', value: 'Simpex Flash Light' },
			{ type: 'drone', label: 'DJI Mavic 2 Pro L1P Drone', value: 'DJI Mavic 2 Pro L1P Drone' },
			{ type: 'lens', label: 'Nikkor Z 24mm/1.8S', value: 'Nikkor Z 24mm/1.8S' },
			{ type: 'batteryAdapter', label: 'DJI Mavic AIR 2 Power Bank Adapter', value: 'DJI Mavic AIR 2 Power Bank Adapter' },
			{ type: 'lens', label: 'Nikkor Z 24-70/2.8S', value: 'Nikkor Z 24-70/2.8S' },
			{ type: 'flash', label: 'Phottix Mitros+ Flash', value: 'Phottix Mitros+ Flash' },
			{ type: 'battery', label: 'Godox VB26 Battery', value: 'Godox VB26 Battery' },
			{ type: 'tripod', label: 'E-Image EH730 Tripod', value: 'E-Image EH730 Tripod' },
			{ type: 'camera', label: 'Nikon Z6 Camera(Supports Atomos)', value: 'Nikon Z6 Camera(Supports Atomos)' },
			{ type: 'doubleStrap', label: 'Double Strap Black Rapid', value: 'Double Strap Black Rapid' },
			{ type: 'mic', label: 'Se', value: 'Double Strap Black Rapid' },
			{ type: 'lens', label: 'Nikkor Z 35/1.4S', value: 'Nikkor Z 35/1.4S' },
			{ type: 'lens', label: 'Nikkor Z 50/1.4S', value: 'Nikkor Z 50/1.4S' },
			{ type: 'camera', label: 'Nikon ZR Camera', value: 'Nikon ZR Camera' },
			{ type: 'ndFilter', label: 'Nisi ND Filter', value: 'Nisi ND Filter' },
		],
	},

	// ============================================================
	// GENERIC EQUIPMENT TYPE KEYS (used for categorization)
	// ============================================================
	equipmentTypes: [
		'atomos',
		'gimbleBattery',
		'camera',
		'drone',
		'lens',
		'tripod',
		'battery',
		'charger',
		'flash',
		'lights',
		'memoryCard',
		'monitor',
		'gimble',
		'microphone',
		'slider',
		'stand',
		'transmitter',
		'receiver',
		'cage',
		'bag',
		'filter',
		'adapter',
		'cable',
		'clamp',
		'diffuser',
		'reflector',
		'softbox',
		'trigger',
		'umbrella',
		'other',
	],
};

export default equipmentConstants;
